import type { PropsWithChildren } from 'react';

import { cn } from '~/chords/lib/cn';
import { variants } from '~/chords/lib/variants';

type Columns = 1 | 2 | 3 | 4 | 5 | 6 | 'fill' | 'fit';

interface GridLayoutProps extends PropsWithChildren {
  /**
   * The maximum number of columns that the layout will have.
   * If this is `fill`, the layout will have as many columns as needed to fill
   * the container including empty columns.
   * If this is `fit`, the layout will have as many columns as needed to fit
   * the content without empty columns.
   *
   * @default 3
   */
  columns?: Columns;
  /**
   * Whether or not to use a fixed number of columns.
   *
   * @default false
   */
  fixed?: boolean;
  /**
   * The gap between the columns.
   *
   * @default 'md'
   */
  gap?: 'sm' | 'md' | 'lg' | 'xl';
  /**
   * The size of the columns in the layout.
   *
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

const grid = variants({
  base: ['grid'],
  variants: {
    columns: {
      1: ['grid-cols-1'],
      2: ['grid-cols-2'],
      3: ['grid-cols-3'],
      4: ['grid-cols-4'],
      5: ['grid-cols-5'],
      6: ['grid-cols-6'],
      fill: '',
      fit: '',
    },
    fixed: {
      true: '',
      false: '',
    },
    gap: {
      sm: ['gap-sm'],
      md: ['gap-md'],
      lg: ['gap-lg'],
      xl: ['gap-xl'],
    },
    size: {
      sm: '',
      md: '',
      lg: '',
      xl: '',
    },
  },
  compoundVariants: [
    {
      columns: 2,
      fixed: false,
      class: ['grid-cols-1', '@sm:grid-cols-2'],
    },
    {
      columns: 3,
      fixed: false,
      class: ['grid-cols-1', '@sm:grid-cols-2', '@md:grid-cols-3'],
    },
    {
      columns: 4,
      fixed: false,
      class: [
        'grid-cols-1',
        '@sm:grid-cols-2',
        '@md:grid-cols-3',
        '@lg:grid-cols-4',
      ],
    },
    {
      columns: 5,
      fixed: false,
      class: [
        'grid-cols-1',
        '@sm:grid-cols-2',
        '@md:grid-cols-3',
        '@lg:grid-cols-4',
        '@xl:grid-cols-5',
      ],
    },
    {
      columns: 6,
      fixed: false,
      class: [
        'grid-cols-1',
        '@sm:grid-cols-2',
        '@md:grid-cols-3',
        '@lg:grid-cols-4',
        '@xl:grid-cols-5',
        '@2xl:grid-cols-6',
      ],
    },
    {
      columns: 'fill',
      fixed: false,
      size: 'sm',
      class: ['grid-cols-fill-sm'],
    },
    {
      columns: 'fill',
      fixed: false,
      size: 'md',
      class: ['grid-cols-fill-md'],
    },
    {
      columns: 'fill',
      fixed: false,
      size: 'lg',
      class: ['grid-cols-fill-lg'],
    },
    {
      columns: 'fill',
      fixed: false,
      size: 'xl',
      class: ['grid-cols-fill-xl'],
    },
    {
      columns: 'fit',
      fixed: false,
      size: 'sm',
      class: ['grid-cols-fit-sm'],
    },
    {
      columns: 'fit',
      fixed: false,
      size: 'md',
      class: ['grid-cols-fit-md'],
    },
    {
      columns: 'fit',
      fixed: false,
      size: 'lg',
      class: ['grid-cols-fit-lg'],
    },
    {
      columns: 'fit',
      fixed: false,
      size: 'xl',
      class: ['grid-cols-fit-xl'],
    },
  ],
});

export function ColumnLayout({
  children,
  columns = 3,
  fixed = false,
  gap = 'md',
  size = 'md',
  ...props
}: GridLayoutProps) {
  return (
    <div {...props} className={cn(grid({ columns, fixed, gap, size }))}>
      {children}
    </div>
  );
}

ColumnLayout.displayName = 'ColumnLayout';
